import React, { useEffect, useMemo, useState } from 'react';
import {
  WxbAlert,
  WxbButton,
  WxbCheckbox,
  WxbEmpty,
  WxbInput,
  WxbInputNumber,
  WxbInspectorPanel,
  WxbPopconfirm,
  WxbSelect,
  WxbTag,
  WxbTextarea,
} from '../wxb-ui';
import type {
  ResourceNode,
  ResourceNodeDraft,
  ResourceRelationship,
  ResourceRelationshipDraft,
  ResourceRelationshipType,
  ResourceTopologyConflict,
} from '../../types/prodResourceTopology';
import {
  parentIdForResource,
  isEligibleCipLoopMember,
  relationshipEndpointKinds,
  relationshipEndpointLabels,
  RELATIONSHIP_TYPE_LABELS,
  RESOURCE_KIND_LABELS,
  validateRelationshipDraft,
} from './resourceTopologyModel';

export type ResourceTopologySelection =
  | { kind: 'node'; id: string }
  | { kind: 'relationship'; id: string }
  | { kind: 'new-node' }
  | { kind: 'new-relationship' }
  | null;

interface NodeFormState extends ResourceNodeDraft {
  parentResourceId: string | null;
  cipCleanable: boolean;
}

interface ResourceTopologyInspectorProps {
  selection: ResourceTopologySelection;
  resources: ResourceNode[];
  relationships: ResourceRelationship[];
  writable: boolean;
  canWrite: boolean;
  saving: boolean;
  conflict: ResourceTopologyConflict | null;
  onReloadConflict: () => void;
  onSaveNode: (
    draft: ResourceNodeDraft,
    parentResourceId: string | null,
    existing: ResourceNode | null,
  ) => Promise<void>;
  onRetireNode: (node: ResourceNode) => Promise<void>;
  onSaveRelationship: (
    draft: ResourceRelationshipDraft,
    existing: ResourceRelationship | null,
  ) => Promise<void>;
  onDeleteRelationship: (relationship: ResourceRelationship) => Promise<void>;
  onDirtyChange: (dirty: boolean) => void;
}

const emptyNodeForm = (): NodeFormState => ({
  code: '',
  name: '',
  kind: 'EQUIPMENT',
  parentResourceId: null,
  lifecycleStatus: 'ACTIVE',
  attributes: null,
  cipCleanable: false,
  note: null,
});

const emptyRelationshipForm = (): ResourceRelationshipDraft => ({
  type: 'PARENT_OF',
  sourceResourceId: '',
  targetResourceId: '',
  priority: null,
  note: null,
});

const nodeToForm = (
  node: ResourceNode,
  relationships: ResourceRelationship[],
): NodeFormState => ({
  code: node.code,
  name: node.name,
  kind: node.kind,
  parentResourceId: parentIdForResource(relationships, node.id),
  lifecycleStatus: node.lifecycleStatus === 'RETIRED' ? 'INACTIVE' : node.lifecycleStatus,
  attributes: node.attributes,
  cipCleanable: node.attributes?.cipCleanable === true,
  note: node.note,
});

const relationToForm = (relation: ResourceRelationship): ResourceRelationshipDraft => ({
  type: relation.type,
  sourceResourceId: relation.sourceResourceId,
  targetResourceId: relation.targetResourceId,
  priority: relation.priority,
  note: relation.note,
});

const ResourceTopologyInspector: React.FC<ResourceTopologyInspectorProps> = ({
  selection,
  resources,
  relationships,
  writable,
  canWrite,
  saving,
  conflict,
  onReloadConflict,
  onSaveNode,
  onRetireNode,
  onSaveRelationship,
  onDeleteRelationship,
  onDirtyChange,
}) => {
  const selectedNode = selection?.kind === 'node'
    ? resources.find((resource) => resource.id === selection.id) ?? null
    : null;
  const selectedRelationship = selection?.kind === 'relationship'
    ? relationships.find((relationship) => relationship.id === selection.id) ?? null
    : null;
  const isNewNode = selection?.kind === 'new-node';
  const isNewRelationship = selection?.kind === 'new-relationship';
  const [editing, setEditing] = useState(isNewNode || isNewRelationship);
  const [dirty, setDirty] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [nodeForm, setNodeForm] = useState<NodeFormState>(() => (
    selectedNode ? nodeToForm(selectedNode, relationships) : emptyNodeForm()
  ));
  const [relationshipForm, setRelationshipForm] = useState<ResourceRelationshipDraft>(() => (
    selectedRelationship ? relationToForm(selectedRelationship) : emptyRelationshipForm()
  ));

  const selectionKey = selection ? `${selection.kind}:${'id' in selection ? selection.id : ''}` : 'none';
  useEffect(() => {
    setEditing(selection?.kind === 'new-node' || selection?.kind === 'new-relationship');
    setDirty(false);
    setFormError(null);
    if (selection?.kind === 'node') {
      const node = resources.find((item) => item.id === selection.id);
      if (node) setNodeForm(nodeToForm(node, relationships));
    } else if (selection?.kind === 'new-node') {
      setNodeForm(emptyNodeForm());
    } else if (selection?.kind === 'relationship') {
      const relation = relationships.find((item) => item.id === selection.id);
      if (relation) setRelationshipForm(relationToForm(relation));
    } else if (selection?.kind === 'new-relationship') {
      setRelationshipForm(emptyRelationshipForm());
    }
    // Only a real selection change resets the form. Workspace refreshes after a 409 must preserve input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const resourceById = useMemo(
    () => new Map(resources.map((resource) => [resource.id, resource])),
    [resources],
  );
  const canEdit = writable && canWrite;
  const physicalParentOptions = resources
    .filter((resource) => resource.id !== selectedNode?.id && resource.kind !== 'CIP_LOOP' && resource.lifecycleStatus !== 'RETIRED')
    .map((resource) => ({ label: `${resource.code} · ${resource.name}`, value: resource.id }));
  const endpointKinds = relationshipEndpointKinds(relationshipForm.type);
  const sourceOptions = resources
    .filter((resource) => endpointKinds.sourceKinds.includes(resource.kind) && resource.lifecycleStatus !== 'RETIRED')
    .map((resource) => ({ label: `${resource.code} · ${resource.name}`, value: resource.id }));
  const targetOptions = resources
    .filter((resource) => {
      if (!endpointKinds.targetKinds.includes(resource.kind) || resource.lifecycleStatus === 'RETIRED') return false;
      if (relationshipForm.type !== 'CIP_LOOP_MEMBER') return true;
      return isEligibleCipLoopMember(resource);
    })
    .map((resource) => ({
      label: `${resource.code} · ${resource.name}${relationshipForm.type === 'CIP_LOOP_MEMBER' && resource.lifecycleStatus === 'INACTIVE' ? ' · 停用（发布时不计有效成员）' : ''}`,
      value: resource.id,
    }));

  const markNode = (patch: Partial<NodeFormState>) => {
    setNodeForm((current) => ({ ...current, ...patch }));
    setDirty(true);
    setFormError(null);
  };
  const markRelationship = (patch: Partial<ResourceRelationshipDraft>) => {
    setRelationshipForm((current) => ({ ...current, ...patch }));
    setDirty(true);
    setFormError(null);
  };

  const cancelEditing = () => {
    if (selectedNode) setNodeForm(nodeToForm(selectedNode, relationships));
    if (selectedRelationship) setRelationshipForm(relationToForm(selectedRelationship));
    setDirty(false);
    setFormError(null);
    setEditing(false);
  };

  const saveNode = async () => {
    if (!nodeForm.code.trim() || !nodeForm.name.trim()) {
      setFormError('资源编码和名称不能为空');
      return;
    }
    const existingAttributes = { ...(nodeForm.attributes ?? {}) };
    delete existingAttributes.equipmentTypeCode;
    const attributes = ['EQUIPMENT', 'COMPONENT'].includes(nodeForm.kind)
      ? {
        ...existingAttributes,
        cipCleanable: nodeForm.cipCleanable,
      }
      : existingAttributes;
    try {
      await onSaveNode({
        code: nodeForm.code.trim(),
        name: nodeForm.name.trim(),
        kind: nodeForm.kind,
        lifecycleStatus: nodeForm.lifecycleStatus,
        attributes,
        note: nodeForm.note?.trim() || null,
      }, nodeForm.kind === 'CIP_LOOP' ? null : nodeForm.parentResourceId, selectedNode);
      setDirty(false);
      setEditing(false);
    } catch {
      // Parent keeps the form mounted and exposes a diagnosable error/conflict banner.
    }
  };

  const saveRelationship = async () => {
    const error = validateRelationshipDraft(relationshipForm, resources);
    if (error) {
      setFormError(error);
      return;
    }
    try {
      await onSaveRelationship({
        ...relationshipForm,
        priority: relationshipForm.type === 'CIP_BACKUP_STATION'
          ? relationshipForm.priority ?? 1
          : null,
        note: relationshipForm.note?.trim() || null,
      }, selectedRelationship);
      setDirty(false);
      setEditing(false);
    } catch {
      // Preserve the draft on request failure.
    }
  };

  const conflictBanner = conflict ? (
    <WxbAlert variant="error" title="草稿已被其他操作更新">
      <p>{conflict.message}，当前输入已保留。</p>
      <WxbButton type="button" variant="secondary" size="sm" onClick={onReloadConflict}>
        读取最新 revision
      </WxbButton>
    </WxbAlert>
  ) : null;

  if (!selection) {
    return (
      <WxbInspectorPanel title="属性检查器" description="选择资源节点或关系后查看和维护属性">
        <WxbEmpty description="尚未选择节点或关系" />
      </WxbInspectorPanel>
    );
  }

  if ((selectedNode || isNewNode) && editing) {
    return (
      <WxbInspectorPanel
        title={isNewNode ? '新建资源节点' : `编辑 ${selectedNode?.code}`}
        description="这里只维护资源身份、节点属性和物理父级"
        status={<WxbTag color="blue">节点</WxbTag>}
      >
        <div className="resource-topology-inspector-form">
          {conflictBanner}
          {formError && <WxbAlert variant="error">{formError}</WxbAlert>}
          <WxbInput
            label="资源编码"
            value={nodeForm.code}
            disabled={!isNewNode}
            onChange={(event) => markNode({ code: event.target.value })}
            placeholder="例如 EQ-BR-01"
          />
          <WxbInput
            label="资源名称"
            value={nodeForm.name}
            onChange={(event) => markNode({ name: event.target.value })}
          />
          <WxbSelect
            label="节点类型"
            value={nodeForm.kind}
            disabled={!isNewNode}
            options={Object.entries(RESOURCE_KIND_LABELS)
              .filter(([value]) => value !== 'CIP_LOOP')
              .map(([value, label]) => ({ value, label }))}
            onChange={(value) => markNode({ kind: value, parentResourceId: nodeForm.parentResourceId })}
          />
          <WxbSelect
            label="物理父级"
            value={nodeForm.parentResourceId ?? undefined}
            allowClear
            showSearch
            optionFilterProp="label"
            options={physicalParentOptions}
            onChange={(value) => markNode({ parentResourceId: value ?? null })}
            placeholder="根节点可不选择"
          />
          <WxbSelect
            label="生命周期"
            value={nodeForm.lifecycleStatus}
            options={[
              { label: '启用', value: 'ACTIVE' },
              { label: '停用', value: 'INACTIVE' },
            ]}
            onChange={(value) => markNode({ lifecycleStatus: value })}
          />
          {['EQUIPMENT', 'COMPONENT'].includes(nodeForm.kind) && (
            <WxbCheckbox
              checked={nodeForm.cipCleanable}
              onChange={(checked) => markNode({ cipCleanable: checked })}
            >
              可作为 CIP 回路成员
            </WxbCheckbox>
          )}
          <WxbTextarea
            label="备注"
            value={nodeForm.note ?? ''}
            onChange={(event) => markNode({ note: event.target.value })}
          />
          <div className="resource-topology-inspector-actions">
            {!isNewNode && (
              <WxbButton type="button" variant="ghost" onClick={cancelEditing} disabled={saving}>
                取消
              </WxbButton>
            )}
            <WxbButton type="button" onClick={saveNode} disabled={saving || !canEdit}>
              {saving ? '保存中…' : '保存节点'}
            </WxbButton>
          </div>
        </div>
      </WxbInspectorPanel>
    );
  }

  if ((selectedRelationship || isNewRelationship) && editing) {
    return (
      <WxbInspectorPanel
        title={isNewRelationship ? '新建资源关系' : '编辑资源关系'}
        description="关系方向由类型固定，不表达工艺时序"
        status={<WxbTag color="cyan">关系</WxbTag>}
      >
        <div className="resource-topology-inspector-form">
          {conflictBanner}
          {formError && <WxbAlert variant="error">{formError}</WxbAlert>}
          <WxbSelect
            label="关系类型"
            value={relationshipForm.type}
            disabled={Boolean(selectedRelationship)}
            options={Object.entries(RELATIONSHIP_TYPE_LABELS)
              .filter(([value]) => value === 'PARENT_OF')
              .map(([value, label]) => ({ value, label }))}
            onChange={(value: ResourceRelationshipType) => markRelationship({
              type: value,
              sourceResourceId: '',
              targetResourceId: '',
              priority: value === 'CIP_BACKUP_STATION' ? 1 : null,
            })}
          />
          <WxbSelect
            label="父节点"
            value={relationshipForm.sourceResourceId || undefined}
            disabled={Boolean(selectedRelationship)}
            showSearch
            optionFilterProp="label"
            options={sourceOptions}
            onChange={(value) => markRelationship({ sourceResourceId: value })}
          />
          <WxbSelect
            label="子节点"
            value={relationshipForm.targetResourceId || undefined}
            disabled={Boolean(selectedRelationship)}
            showSearch
            optionFilterProp="label"
            options={targetOptions}
            onChange={(value) => markRelationship({ targetResourceId: value })}
          />
          {relationshipForm.type === 'CIP_BACKUP_STATION' && (
            <WxbInputNumber
              label="备站优先级"
              min={1}
              precision={0}
              value={relationshipForm.priority ?? 1}
              onChange={(value) => markRelationship({ priority: typeof value === 'number' ? value : 1 })}
            />
          )}
          <WxbTextarea
            label="备注"
            value={relationshipForm.note ?? ''}
            onChange={(event) => markRelationship({ note: event.target.value })}
          />
          <div className="resource-topology-inspector-actions">
            {!isNewRelationship && (
              <WxbButton type="button" variant="ghost" onClick={cancelEditing} disabled={saving}>
                取消
              </WxbButton>
            )}
            <WxbButton type="button" onClick={saveRelationship} disabled={saving || !canEdit}>
              {saving ? '保存中…' : '保存关系'}
            </WxbButton>
          </div>
        </div>
      </WxbInspectorPanel>
    );
  }

  if (selectedNode) {
    const parentId = parentIdForResource(relationships, selectedNode.id);
    const parent = parentId ? resourceById.get(parentId) : null;
    return (
      <WxbInspectorPanel
        title={selectedNode.name}
        description={selectedNode.code}
        status={<WxbTag color={selectedNode.kind === 'CIP_LOOP' ? 'cyan' : 'blue'}>{RESOURCE_KIND_LABELS[selectedNode.kind]}</WxbTag>}
        sections={[
          {
            key: 'identity',
            title: '节点属性',
            rows: [
              { key: 'lifecycle', label: '生命周期', value: selectedNode.lifecycleStatus === 'ACTIVE' ? '启用' : selectedNode.lifecycleStatus === 'INACTIVE' ? '停用' : '已退役' },
              { key: 'parent', label: '物理父级', value: selectedNode.kind === 'CIP_LOOP' ? '不适用' : parent ? `${parent.code} · ${parent.name}` : '根节点' },
              ...(['EQUIPMENT', 'COMPONENT'].includes(selectedNode.kind)
                ? [{ key: 'cip-cleanable', label: 'CIP 清洗拓扑', value: selectedNode.attributes?.cipCleanable === true ? '纳入' : '不纳入' }]
                : []),
              { key: 'note', label: '备注', value: selectedNode.note || '—' },
            ],
          },
        ]}
        footer={canEdit && selectedNode.lifecycleStatus !== 'RETIRED' ? (
          <div className="resource-topology-inspector-actions">
            <WxbPopconfirm
              title={`退役资源 ${selectedNode.code}？`}
              description="退役后资源编码保留，已有关系需要通过校验处理。"
              okText="确认退役"
              onConfirm={() => onRetireNode(selectedNode)}
            >
              <WxbButton type="button" variant="danger" size="sm" disabled={saving}>
                退役
              </WxbButton>
            </WxbPopconfirm>
            <WxbButton type="button" size="sm" onClick={() => setEditing(true)}>
              编辑节点
            </WxbButton>
          </div>
        ) : undefined}
      />
    );
  }

  if (selectedRelationship) {
    const endpointLabels = relationshipEndpointLabels(selectedRelationship.type);
    const source = resourceById.get(selectedRelationship.sourceResourceId);
    const target = resourceById.get(selectedRelationship.targetResourceId);
    return (
      <WxbInspectorPanel
        title={RELATIONSHIP_TYPE_LABELS[selectedRelationship.type]}
        description={`${source?.code ?? '未知节点'} → ${target?.code ?? '未知节点'}`}
        status={<WxbTag color="cyan">关系</WxbTag>}
        sections={[
          {
            key: 'relationship',
            title: '关系属性',
            rows: [
              { key: 'source', label: endpointLabels.source, value: source ? `${source.code} · ${source.name}` : '未知节点' },
              { key: 'target', label: endpointLabels.target, value: target ? `${target.code} · ${target.name}` : '未知节点' },
              ...(selectedRelationship.type === 'CIP_BACKUP_STATION'
                ? [{ key: 'priority', label: '备站优先级', value: selectedRelationship.priority ?? 1 }]
                : []),
              { key: 'note', label: '备注', value: selectedRelationship.note || '—' },
            ],
          },
        ]}
        footer={canEdit ? (
          <div className="resource-topology-inspector-actions">
            <WxbPopconfirm
              title={`移除“${RELATIONSHIP_TYPE_LABELS[selectedRelationship.type]}”关系？`}
              description="只移除关系，不会退役两端资源节点。"
              okText="确认移除"
              onConfirm={() => onDeleteRelationship(selectedRelationship)}
            >
              <WxbButton type="button" variant="danger" size="sm" disabled={saving}>
                移除关系
              </WxbButton>
            </WxbPopconfirm>
            <WxbButton type="button" size="sm" onClick={() => setEditing(true)}>
              编辑关系
            </WxbButton>
          </div>
        ) : undefined}
      />
    );
  }

  return (
    <WxbInspectorPanel title="属性检查器">
      <WxbEmpty description="所选对象已不存在，请重新选择" />
    </WxbInspectorPanel>
  );
};

export default ResourceTopologyInspector;
